// src/components/SPanel/StudentVideoLibrary.jsx
//
// Videoclipurile recomandate, văzute de elev (și de instructor). Ca Testare și
// calendarul elevului: fără panou, sus rândul comun (.pageToolbar) cu titlul și
// căutarea (SearchBarUI), dedesubt plăcuțele de sticlă ale videoclipurilor, ca pe
// pagina principală (.wd-video). Clic pe plăcuță = player-ul încorporat, cu
// „Înapoi” și „Deschide pe YouTube” (ButtonUI); stările — StatusUI.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getRecommendedVideos } from "../../api/recommendedVideosService";
import {
   mapRecommendedVideo,
   youtubeEmbedUrl,
   youtubeThumbnailUrl,
} from "../../utils/youtube";
import { ButtonUI, SearchBarUI, StatusUI } from "../UI";
import { pressPoint } from "../UI/glass";

const PAGE_LIMIT = 50;
const MAX_PAGES = 20;
const PLAY_PATH = "M8 5.14v14l11-7z";

async function fetchAllRecommendedVideos() {
   const out = [];
   for (let page = 1; page <= MAX_PAGES; page += 1) {
      const result = await getRecommendedVideos({ page, limit: PAGE_LIMIT });
      const items = Array.isArray(result?.items) ? result.items : [];
      out.push(...items);

      const total = Number(result?.total);
      const reachedTotal = Number.isFinite(total) && out.length >= total;
      if (reachedTotal || items.length < PAGE_LIMIT) break;
   }
   return out;
}

function openOnYoutube(video) {
   const href = String(video?.watchUrl || "").trim();
   if (!href || typeof window === "undefined") return;
   window.open(href, "_blank", "noopener,noreferrer");
}

// miniatura 16:9 fără benzi negre (hq720); unele videoclipuri n-o au: mqdefault
function VideoImage({ video }) {
   return (
      <img
         className="sVideo__img"
         src={youtubeThumbnailUrl(video.youtubeId, "hq720")}
         alt=""
         loading="lazy"
         onError={(event) => {
            const fallback = youtubeThumbnailUrl(video.youtubeId, "mqdefault");
            if (event.currentTarget.src !== fallback) event.currentTarget.src = fallback;
         }}
      />
   );
}

export default function StudentVideoLibrary() {
   const [videos, setVideos] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const [searchText, setSearchText] = useState("");
   const [activeVideoId, setActiveVideoId] = useState("");
   // locul din listă, ca „Înapoi” să întoarcă la plăcuța de unde ai pornit
   const listScrollRef = useRef(0);

   const load = useCallback(async () => {
      setLoading(true);
      setError("");
      try {
         const rows = await fetchAllRecommendedVideos();
         setVideos(
            rows
               .map((row, index) => mapRecommendedVideo(row, index))
               .filter(Boolean),
         );
      } catch (e) {
         setVideos([]);
         setError(e?.message || "Nu s-au putut încărca videoclipurile.");
      } finally {
         setLoading(false);
      }
   }, []);

   useEffect(() => {
      load();
   }, [load]);

   const filteredVideos = useMemo(() => {
      const query = String(searchText || "")
         .trim()
         .toLowerCase();
      if (!query) return videos;
      return videos.filter((video) =>
         `${video.title || ""} ${video.youtubeId || ""}`
            .toLowerCase()
            .includes(query),
      );
   }, [searchText, videos]);

   const activeVideo = useMemo(
      () =>
         videos.find((video) => String(video.id) === String(activeVideoId)) ||
         null,
      [activeVideoId, videos],
   );

   const handleWatch = (video) => {
      listScrollRef.current = window.scrollY;
      setActiveVideoId(String(video.id));
      window.scrollTo(0, 0);
   };
   const handleBackToList = () => {
      setActiveVideoId("");
      requestAnimationFrame(() => window.scrollTo(0, listScrollRef.current));
   };

   if (activeVideo) {
      return (
         <div className="sVideo">
            <div className="pageToolbar sVideo__toolbar sVideo__toolbar--watch">
               <ButtonUI size="lg" variant="subtle" icon="arrow" onClick={handleBackToList}>
                  Înapoi
               </ButtonUI>
               <div className="pageToolbar__controls">
                  <ButtonUI size="lg" variant="primary" onClick={() => openOnYoutube(activeVideo)}>
                     Deschide pe YouTube
                  </ButtonUI>
               </div>
            </div>

            <section className="sVideo__watch" aria-labelledby="sVideo-watch-title">
               <div className="sVideo__player">
                  <iframe
                     key={activeVideo.youtubeId}
                     src={youtubeEmbedUrl(activeVideo.youtubeId, { autoplay: true })}
                     title={activeVideo.title}
                     allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                     allowFullScreen
                     referrerPolicy="strict-origin-when-cross-origin"
                  />
               </div>
               <h2 id="sVideo-watch-title" className="sVideo__watchTitle">
                  {activeVideo.title}
               </h2>
            </section>
         </div>
      );
   }

   return (
      <div className="sVideo">
         <div className="pageToolbar sVideo__toolbar">
            <h1 className="sVideo__heading">Videoclipuri</h1>
            <SearchBarUI
               surface="page"
               value={searchText}
               onChange={(e) => setSearchText(e.target.value)}
               onClear={() => setSearchText("")}
               placeholder="Caută videoclip"
               aria-label="Caută videoclip"
               autoComplete="off"
               className="pageToolbar__search"
            />
         </div>

         {loading ? (
            <StatusUI tone="loading" look="block" />
         ) : error ? (
            <StatusUI
               tone="error"
               look="block"
               action={
                  <ButtonUI variant="subtle" onClick={load}>
                     Reîncearcă
                  </ButtonUI>
               }
            >
               {error}
            </StatusUI>
         ) : !filteredVideos.length ? (
            <StatusUI tone="empty" look="block">
               {videos.length
                  ? "Nu există videoclipuri pentru filtrul curent."
                  : "Nu există încă videoclipuri recomandate."}
            </StatusUI>
         ) : (
            <ul className="sVideo__grid">
               {filteredVideos.map((video) => (
                  <li key={video.id} className="sVideo__item">
                     <button
                        type="button"
                        className="sVideo__card"
                        onClick={() => handleWatch(video)}
                        onPointerDown={pressPoint}
                        aria-label={`Urmărește ${video.title}`}
                     >
                        <VideoImage video={video} />
                        <span className="sVideo__play" aria-hidden="true">
                           <svg viewBox="0 0 24 24">
                              <path d={PLAY_PATH} />
                           </svg>
                        </span>
                        <span className="sVideo__title" title={video.title}>
                           {video.title}
                        </span>
                     </button>
                     <ButtonUI
                        size="sm"
                        className="sVideo__youtube"
                        onClick={() => openOnYoutube(video)}
                        aria-label={`Deschide pe YouTube: ${video.title}`}
                     >
                        YouTube
                     </ButtonUI>
                  </li>
               ))}
            </ul>
         )}
      </div>
   );
}
